import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { PDFDocument } from 'pdf-lib';
import './MikaFileManager.scss';

export interface MikaFile {
    id: number;
    name: string;
    url: string;
    group?: string;
}

export interface UploadSlot {
    key: string;
    label: string;
    icon: 'file' | 'files' | 'image' | 'alert';
    accept?: string;
    multiple?: boolean;
}

interface MikaFileManagerProps {
    files: MikaFile[];
    uploadSlots?: UploadSlot[];
    onUpload?: (key: string, files: File[]) => void;
    onDelete?: (fileId: number) => void;
    onBulkDelete?: (fileIds: number[]) => void;
    groups?: { key: string; label: string; color?: string }[];
    loading?: boolean;
    toolbarUpload?: { accept?: string; multiple?: boolean; onUpload: (files: File[]) => void };
}

const getFileExt = (name: string) => name.split('.').pop()?.toLowerCase() || '';

const isImage = (name: string) => ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(getFileExt(name));
const isPdf = (name: string) => getFileExt(name) === 'pdf';

const FileIcon: React.FC<{ name: string; url: string }> = ({ name, url }) => {
    if (isImage(name)) {
        return <img src={url} alt="" className="mfm-file-thumb" />;
    }
    if (isPdf(name)) {
        return (
            <span className="mfm-file-icon-box mfm-file-icon-pdf">
                <span className="mfm-file-ext">PDF</span>
            </span>
        );
    }
    return (
        <span className="mfm-file-icon-box mfm-file-icon-default">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
        </span>
    );
};

const SLOT_ICONS: Record<string, React.ReactNode> = {
    file: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>,
    files: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></svg>,
    image: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>,
    alert: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>,
};

const MikaFileManager: React.FC<MikaFileManagerProps> = ({
    files,
    uploadSlots,
    onUpload,
    onDelete,
    onBulkDelete,
    groups,
    loading: externalLoading,
    toolbarUpload,
}) => {
    const intl = useIntl();
    const fm = (id: string, defaultMessage: string) => intl.formatMessage({ id, defaultMessage });

    const [selected, setSelected] = useState<Set<number>>(new Set());
    const [uploadState, setUploadState] = useState<Record<string, { names: string[]; files: File[] }>>({});
    const [merging, setMerging] = useState(false);
    const [busy, setBusy] = useState(false);

    const toggleSelect = (id: number) => {
        setSelected(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const toggleAll = (groupFiles: MikaFile[]) => {
        const ids = groupFiles.map(f => f.id);
        const allSelected = ids.every(id => selected.has(id));
        setSelected(prev => {
            const next = new Set(prev);
            ids.forEach(id => allSelected ? next.delete(id) : next.add(id));
            return next;
        });
    };

    const handleDownload = async (url: string, name: string) => {
        try {
            const bytes = await fetchFileBytes(url);
            const blob = new Blob([bytes]);
            const blobUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = name;
            a.click();
            URL.revokeObjectURL(blobUrl);
        } catch (err) {
            // Fallback: open in new tab
            window.open(url, '_blank');
        }
    };

    const handleBulkDownload = (groupFiles: MikaFile[]) => {
        const toDownload = groupFiles.filter(f => selected.has(f.id));
        toDownload.forEach((f, i) => {
            setTimeout(() => handleDownload(f.url, f.name), i * 300);
        });
    };

    const handleBulkDelete = (groupFiles: MikaFile[]) => {
        const ids = groupFiles.filter(f => selected.has(f.id)).map(f => f.id);
        if (ids.length === 0) return;
        if (onBulkDelete) {
            onBulkDelete(ids);
        } else if (onDelete) {
            ids.forEach(id => onDelete(id));
        }
        setSelected(prev => {
            const next = new Set(prev);
            ids.forEach(id => next.delete(id));
            return next;
        });
    };

    const handleDeleteWithBusy = async (fileId: number) => {
        if (busy || !onDelete) return;
        setBusy(true);
        try { await onDelete(fileId); } finally { setBusy(false); }
    };

    const handleBulkDeleteWithBusy = async (groupFiles: MikaFile[]) => {
        const ids = groupFiles.filter(f => selected.has(f.id)).map(f => f.id);
        if (ids.length === 0 || busy) return;
        setBusy(true);
        try {
            if (onBulkDelete) {
                await onBulkDelete(ids);
            } else if (onDelete) {
                for (const id of ids) await onDelete(id);
            }
            setSelected(prev => {
                const next = new Set(prev);
                ids.forEach(id => next.delete(id));
                return next;
            });
        } finally { setBusy(false); }
    };

    const handleUploadChange = (slot: UploadSlot, fileList: FileList | null) => {
        if (!fileList?.length) return;
        const arr = Array.from(fileList);
        setUploadState(prev => ({
            ...prev,
            [slot.key]: { names: arr.map(f => f.name), files: arr }
        }));
        if (onUpload) onUpload(slot.key, arr);
    };

    // ── PDF merge ────────────────────────────────────────

    const fetchFileBytes = async (url: string): Promise<Uint8Array> => {
        const token = localStorage.getItem('mikaBearerToken');
        const storageMatch = url.match(/\/storage\/(.+)$/);
        let fetchUrl = url;
        if (storageMatch) {
            const apiBase = url.substring(0, url.indexOf('/storage/'));
            const decodedPath = decodeURIComponent(storageMatch[1]);
            fetchUrl = `${apiBase}/api/v1/file-proxy?path=${encodeURIComponent(decodedPath)}`;
        }
        const response = await fetch(fetchUrl, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        });
        if (!response.ok) throw new Error(`Failed to fetch ${url}: ${response.status}`);
        return new Uint8Array(await response.arrayBuffer());
    };

    const handleViewAllMerged = async (targetFiles?: MikaFile[]) => {
        const filesToMerge = targetFiles || files;
        if (filesToMerge.length === 0) return;
        setMerging(true);
        try {
            const mergedPdf = await PDFDocument.create();
            const A4_WIDTH = 595.28;
            const A4_HEIGHT = 841.89;
            const PADDING = 40;

            for (const file of filesToMerge) {
                try {
                    const bytes = await fetchFileBytes(file.url);
                    const ext = getFileExt(file.name);

                    if (isPdf(file.name)) {
                        const srcPdf = await PDFDocument.load(bytes, { ignoreEncryption: true });
                        const copiedPages = await mergedPdf.copyPages(srcPdf, srcPdf.getPageIndices());
                        copiedPages.forEach(function(page) { mergedPdf.addPage(page); });
                    } else if (ext === 'png') {
                        const img = await mergedPdf.embedPng(bytes);
                        const page = mergedPdf.addPage([A4_WIDTH, A4_HEIGHT]);
                        const maxW = A4_WIDTH - PADDING * 2;
                        const maxH = A4_HEIGHT - PADDING * 2;
                        const scale = Math.min(maxW / img.width, maxH / img.height, 1);
                        const w = img.width * scale;
                        const h = img.height * scale;
                        page.drawImage(img, { x: (A4_WIDTH - w) / 2, y: (A4_HEIGHT - h) / 2, width: w, height: h });
                    } else if (ext === 'jpg' || ext === 'jpeg') {
                        const img = await mergedPdf.embedJpg(bytes);
                        const page = mergedPdf.addPage([A4_WIDTH, A4_HEIGHT]);
                        const maxW = A4_WIDTH - PADDING * 2;
                        const maxH = A4_HEIGHT - PADDING * 2;
                        const scale = Math.min(maxW / img.width, maxH / img.height, 1);
                        const w = img.width * scale;
                        const h = img.height * scale;
                        page.drawImage(img, { x: (A4_WIDTH - w) / 2, y: (A4_HEIGHT - h) / 2, width: w, height: h });
                    } else if (ext === 'webp' || ext === 'gif' || ext === 'bmp') {
                        // Convert unsupported formats to PNG via canvas
                        var imgEl = new Image();
                        var imgBytes = await new Promise<Uint8Array>(function(resolve, reject) {
                            imgEl.onload = function() {
                                var canvas = document.createElement('canvas');
                                canvas.width = imgEl.width;
                                canvas.height = imgEl.height;
                                var ctx = canvas.getContext('2d');
                                if (ctx) ctx.drawImage(imgEl, 0, 0);
                                canvas.toBlob(function(blob) {
                                    if (!blob) { reject(new Error('Canvas blob failed')); return; }
                                    blob.arrayBuffer().then(function(buf) { resolve(new Uint8Array(buf)); });
                                }, 'image/png');
                            };
                            imgEl.onerror = reject;
                            imgEl.src = URL.createObjectURL(new Blob([bytes]));
                        });
                        var img = await mergedPdf.embedPng(imgBytes);
                        var page = mergedPdf.addPage([A4_WIDTH, A4_HEIGHT]);
                        var maxW = A4_WIDTH - PADDING * 2;
                        var maxH = A4_HEIGHT - PADDING * 2;
                        var scale = Math.min(maxW / img.width, maxH / img.height, 1);
                        var w = img.width * scale;
                        var h = img.height * scale;
                        page.drawImage(img, { x: (A4_WIDTH - w) / 2, y: (A4_HEIGHT - h) / 2, width: w, height: h });
                    }
                    // else: skip unsupported format
                } catch (fileErr) {
                    console.warn('Skipping file in PDF merge:', file.name, fileErr);
                    // Continue with next file instead of aborting
                }
            }

            const pdfBytes = await mergedPdf.save();
            const blob = new Blob([pdfBytes], { type: 'application/pdf' });
            window.open(URL.createObjectURL(blob), '_blank');
        } catch (err) {
            console.error('PDF merge error:', err);
        } finally {
            setMerging(false);
        }
    };

    // ── Render group ─────────────────────────────────────

    const renderGroup = (groupFiles: MikaFile[], label: string, color?: string) => {
        if (groupFiles.length === 0 && !toolbarUpload) return null;
        const groupSelected = groupFiles.filter(f => selected.has(f.id));
        const allSelected = groupFiles.length > 0 && groupFiles.every(f => selected.has(f.id));

        return (
            <div className="mfm-group" key={label}>
                <div className="mfm-group-toolbar">
                    <div className="mfm-toolbar-left">
                        <label className="mfm-checkbox-label">
                            <input type="checkbox" checked={allSelected} onChange={() => toggleAll(groupFiles)} />
                            <span className="mfm-group-label" style={color ? { color } : undefined}>{label}</span>
                            <span className="mfm-group-count">{groupFiles.length}</span>
                        </label>
                    </div>
                    <div className="mfm-toolbar-right">
                        {toolbarUpload && (
                            <label className="mfm-toolbar-btn mfm-toolbar-btn-upload" style={{ cursor: 'pointer' }} title={fm('common.file.upload', 'Upload')}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
                                {fm('common.file.upload', 'Upload')}
                                <input type="file" style={{ display: 'none' }} accept={toolbarUpload.accept || '*'} multiple={toolbarUpload.multiple !== false}
                                    onChange={function(e) {
                                        if (!e.target.files || e.target.files.length === 0) return;
                                        toolbarUpload.onUpload(Array.from(e.target.files));
                                        e.target.value = '';
                                    }} />
                            </label>
                        )}
                        <button className="mfm-toolbar-btn mfm-toolbar-btn-pdf" onClick={() => handleViewAllMerged(groupFiles)} disabled={merging} type="button" title={fm('common.file.viewPdf', 'View in PDF')}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                            {merging ? '...' : 'PDF'}
                        </button>
                        {groupSelected.length > 0 && (
                            <>
                                <button className="mfm-toolbar-btn mfm-toolbar-btn-download" onClick={() => handleBulkDownload(groupFiles)} type="button" title={fm('common.file.download', 'Download')}>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                                    {groupSelected.length}
                                </button>
                                <button className="mfm-toolbar-btn mfm-toolbar-btn-delete" onClick={() => handleBulkDeleteWithBusy(groupFiles)} type="button" title={fm('common.file.delete', 'Delete')} disabled={busy}>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" /></svg>
                                    {groupSelected.length}
                                </button>
                            </>
                        )}
                    </div>
                </div>
                <div className="mfm-file-list">
                    {groupFiles.map((f, i) => {
                        return (
                            <div key={f.id} className={`mfm-file-row ${selected.has(f.id) ? 'mfm-file-row-selected' : ''} ${i % 2 === 1 ? 'mfm-file-row-alt' : ''}`}>
                                <input type="checkbox" className="mfm-file-check" checked={selected.has(f.id)} onChange={() => toggleSelect(f.id)} />
                                <FileIcon name={f.name} url={f.url} />
                                <a href={f.url} target="_blank" rel="noopener noreferrer" className="mfm-file-name">{f.name}</a>
                                <div className="mfm-file-actions">
                                    <button className="mfm-action-btn mfm-action-btn-download" onClick={() => handleDownload(f.url, f.name)} type="button" title={fm('common.file.download', 'Download')}>
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                                    </button>
                                    {onDelete && (
                                        <button className="mfm-action-btn mfm-action-btn-danger" onClick={() => handleDeleteWithBusy(f.id)} type="button" title={fm('common.file.delete', 'Delete')} disabled={busy}>
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" /></svg>
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    // Group files
    const groupedFiles: Record<string, MikaFile[]> = {};
    if (groups) {
        groups.forEach(g => { groupedFiles[g.key] = []; });
        files.forEach(f => {
            const g = f.group || 'default';
            if (!groupedFiles[g]) groupedFiles[g] = [];
            groupedFiles[g].push(f);
        });
    } else {
        groupedFiles['default'] = files;
    }

    const isLoading = busy || externalLoading;

    return (
        <div className={`mfm-container ${isLoading ? 'mfm-busy' : ''}`}>
            {isLoading && (
                <div className="mfm-busy-overlay">
                    <div className="mfm-busy-spinner" />
                </div>
            )}
            {/* Upload slots */}
            {uploadSlots && uploadSlots.length > 0 && (
                <div className="mfm-upload-grid mfm-upload-grid-2">
                    {uploadSlots.map(slot => {
                        const us = uploadState[slot.key];
                        const hasFiles = us && us.names.length > 0;
                        return (
                            <label key={slot.key} className={`mfm-upload-slot ${hasFiles ? 'mfm-upload-slot-active' : ''}`}>
                                <input type="file" className="mfm-upload-input" multiple={slot.multiple} accept={slot.accept}
                                    onChange={e => handleUploadChange(slot, e.target.files)} />
                                <div className="mfm-upload-icon">{SLOT_ICONS[slot.icon]}</div>
                                <div className="mfm-upload-text">
                                    <span className="mfm-upload-label">{slot.label}</span>
                                    {hasFiles
                                        ? <span className="mfm-upload-filename">{us.names.length === 1 ? us.names[0] : `${us.names.length} ${fm('common.file.filesCount', 'files')}`}</span>
                                        : <span className="mfm-upload-hint">{slot.multiple ? fm('common.file.selectFiles', 'Select files...') : fm('common.file.selectFile', 'Select file...')}</span>
                                    }
                                </div>
                                {hasFiles && <span className="mfm-upload-check"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg></span>}
                            </label>
                        );
                    })}
                </div>
            )}

            {/* File groups */}
            {groups
                ? groups.map(g => renderGroup(groupedFiles[g.key] || [], g.label, g.color))
                : (files.length > 0 || toolbarUpload) && renderGroup(files, fm('common.file.files', 'Files'), undefined)
            }
        </div>
    );
};

export default MikaFileManager;
